/**
 * Dashboard KPIs for the new console, per role, from the v109 handoff (§9 Sales, §10 Billing, §11.1 Editing; Admin
 * sees the union). Pure: rows in, cards out. Rule §2: "Dashboard KPI counts and their destination queues must use
 * the same eligibility/query logic" — every card names the queue it opens and the rule it counts by, and the queue
 * pages import these same predicates when they arrive.
 *
 * Inputs
 *   balances  booking_balances rows (the one balance formula; carries is_active / is_placeholder / needs_backfill)
 *   bookings  bookings rows: id, created_at, event_date, booking_status, review_status, media_delivered_at,
 *             logistics_completed, add_ons, personalization_data, booking_addons, reviews
 *   payments  payments rows this month: amount, payment_date, status
 *   today     YYYY-MM-DD in the business time zone
 */
import { describeDeadline } from '../editing/deadlines.js'
import { cancelledOn, isPaidPayment, isCashRefund, isRealBooking, businessDate } from '../reporting/reportModel.js'

const dayMs = 86400000
const dateOnly = v => (v ? String(v).slice(0, 10) : '')
const noon = iso => Date.parse(iso + 'T12:00:00Z')
const addDays = (iso, n) => new Date(noon(iso) + n * dayMs).toISOString().slice(0, 10)
const monthOf = iso => businessDate(iso).slice(0, 7)
/** 'YYYY-MM' moved by n months. */
const shiftMonth = (ym, n) => { const [y, m] = String(ym).split('-').map(Number); const d = new Date(Date.UTC(y, m - 1 + n, 1)); return d.toISOString().slice(0, 7) }
const CLOSED = new Set(['cancelled', 'canceled', 'completed', 'archived'])

/** A booking that counts: on the ledger view as active, not a placeholder (Rose 2099 rows, test bookings). */
export function isCountable(bal) {
  return Boolean(bal) && bal.is_active !== false && !bal.is_placeholder
}

export function isActiveBooking(bal) {
  return isCountable(bal) && !CLOSED.has(String(bal.booking_status || '').toLowerCase())
}

export function eventWithin(bal, today, days) {
  const d = dateOnly(bal?.event_date)
  return Boolean(d) && d >= today && d <= addDays(today, days)
}

/**
 * Bookings MTD with Traci's three views (23 Sep 2026): new this month; booked this month AND cancelled this month;
 * every cancellation this month (booked any time). Same cancellation rule as Reports & Analytics.
 */
export function bookingsMtdVariants(bookings, balById, today) {
  const month = monthOf(today)
  const real = bookings.filter(b => isRealBooking(b, balById))
  const created = real.filter(b => monthOf(b.created_at) === month)
  const cancelledThisMonth = real.filter(b => monthOf(cancelledOn(b)) === month)
  return {
    new: created.map(b => b.id),
    booked_cancelled: created.filter(b => monthOf(cancelledOn(b)) === month).map(b => b.id),
    cancelled: cancelledThisMonth.map(b => b.id)
  }
}

/**
 * Revenue MTD with Traci's four views: all money in this month; money in this month on bookings made this month;
 * refunds this month on bookings made this month; every cash refund this month. Same payment and refund rules as
 * Reports & Analytics (price reductions are not refunds).
 */
export function revenueMtdVariants(payments, refunds, bookings, balById, today) {
  const month = monthOf(today)
  const byId = new Map(bookings.filter(b => isRealBooking(b, balById)).map(b => [b.id, b]))
  const newBooking = id => monthOf(byId.get(id)?.created_at) === month
  const paid = payments.filter(p => isPaidPayment(p) && monthOf(p.payment_date || p.created_at) === month && byId.has(p.booking_id))
  const refunded = refunds.filter(x => isCashRefund(x) && monthOf(x.refunded_at || x.created_at) === month)
  const cents = p => Math.round(Number(p.amount || 0) * 100)
  const sum = (arr, f) => arr.reduce((s, x) => s + f(x), 0)
  return {
    all: sum(paid, cents),
    new: sum(paid.filter(p => newBooking(p.booking_id)), cents),
    booked_refunded: sum(refunded.filter(x => newBooking(x.booking_id)), x => Number(x.amount_cents || 0)),
    refunds: sum(refunded, x => Number(x.amount_cents || 0))
  }
}

export const BOOKINGS_MTD_VIEWS = [
  { key: 'new', label: 'New this month' },
  { key: 'booked_cancelled', label: 'Booked & cancelled this month' },
  { key: 'cancelled', label: 'Cancelled this month' }
]
export const REVENUE_MTD_VIEWS = [
  { key: 'all', label: 'All revenue' },
  { key: 'new', label: 'New bookings' },
  { key: 'booked_refunded', label: 'Booked & refunded this month' },
  { key: 'refunds', label: 'All refunds this month' }
]

/**
 * `activeIds`: the Bookings tab's Active list (Traci, Oct 2026: "# of active bookings should match what is on the
 * Bookings tab"). When given, Active Bookings and the event-window cards count exactly those bookings, with their money
 * from the balance view; without it, the balance view's own active flag (older callers and tests).
 */
export function computeKpis({ balances = [], bookings = [], payments = [], refunds = [], upcoming = null, editing = null, today, activeIds = null, unfinishedCheckouts = null }) {
  const byId = new Map(bookings.map(b => [b.id, b]))
  const balanceOf = new Map(balances.map(b => [b.booking_id, b]))
  const active = activeIds
    ? [...activeIds].map(id => { const bk = byId.get(id) || {}; const bal = balanceOf.get(id) || {}; return { ...bal, booking_id: id, event_date: bk.event_date || bal.event_date, booking_status: bk.booking_status || bal.booking_status } })
    : balances.filter(isActiveBooking)
  const month = monthOf(today)

  const next7 = active.filter(b => eventWithin(b, today, 7))
  const next31 = active.filter(b => eventWithin(b, today, 31))
  // Traci's doc, Oct 2026: "# events total for the current month" (not the next 31 days): every real, uncancelled booking
  // whose event falls in this calendar month, days already past included
  const eventsIn = m => bookings.filter(b => isRealBooking(b, balanceOf) && !cancelledOn(b) && !String(b.booking_status || balanceOf.get(b.id)?.booking_status || '').toLowerCase().startsWith('cancel') && dateOnly(b.event_date).slice(0, 7) === m)
  const eventsThisMonth = eventsIn(month)
  // Traci's Loom (28 Sep 2026): Admin and Billing pick last month, this month or next month
  const eventMonths = [{ key: 'last', label: 'Last month', month: shiftMonth(month, -1) }, { key: 'this', label: 'This month', month }, { key: 'next', label: 'Next month', month: shiftMonth(month, 1) }]
  const arCents = active.filter(b => !b.needs_backfill).reduce((s, b) => s + Number(b.remaining_cents || 0), 0)

  // Sales-owned pre-event action today: the logistics questionnaire is not complete (§9). The GHL queue needs its
  // own entity before it can count (§9, §16), so it is not part of this number yet.
  const salesNext7 = next7.filter(b => byId.get(b.booking_id)?.logistics_completed === false)
  // Billing-owned pre-event action: money still owed (§10).
  const billingNext7 = next7.filter(b => Number(b.remaining_cents || 0) > 0 && !b.needs_backfill)

  // Editing (§11.1): fulfillment is every countable booking whose event has passed and is not delivered.
  const fulfillment = balances.filter(b =>
    isCountable(b)
    && dateOnly(b.event_date) && dateOnly(b.event_date) < today
    && !['cancelled', 'canceled'].includes(String(b.booking_status || '').toLowerCase())
    && byId.get(b.booking_id)?.review_status !== 'delivered')
  const overdueEditing = fulfillment.filter(b => describeDeadline(byId.get(b.booking_id) || { event_date: b.event_date }, 'photo', today).risk === 'overdue')
  // Same rule as the Editing board (H§11.1: counts and queues never disagree): its Initial Review queue and its overdue rows.
  const pendingInitialReview = editing ? editing.queues.initial_review : fulfillment.filter(b => byId.get(b.booking_id)?.review_status === 'pending_review')
  const overdueEditingCount = editing ? editing.overdue : overdueEditing.length
  const deliveredMtd = bookings.filter(b => monthOf(b.media_delivered_at) === month)

  const balById = new Map(balances.map(b => [b.booking_id, b]))
  const bookingViews = bookingsMtdVariants(bookings, balById, today)
  const revenueViews = revenueMtdVariants(payments, refunds, bookings, balById, today)
  const outstandingNext7 = upcoming ? upcoming.filter(u => !u.ready).length : null

  const dollars = c => '$' + Math.round(c / 100).toLocaleString('en-US')
  const card = (key, label, value, queue, rule, hint = null) => ({ key, label, value, queue, rule, hint })

  const cards = {
    active_bookings: card('active_bookings', 'Active Bookings', active.length, 'bookings', activeIds ? 'same as the Active list on the Bookings tab: not completed, cancelled, a test or a Rose intake' : 'active on the ledger view, not a placeholder, not cancelled or completed'),
    events_next_7: card('events_next_7', 'Events Next 7 Days', next7.length, 'bookings', 'active, event date within 7 days'),
    events_next_31: card('events_next_31', 'Events Next 31 Days', next31.length, 'bookings', 'active, event date within 31 days'),
    events_this_month: { ...card('events_this_month', 'Events by Month', eventsThisMonth.length, 'bookings', 'every booking with its event in that calendar month, past days included (cancelled and test bookings excluded)'), views: eventMonths.map(v => ({ key: v.key, label: v.label, value: eventsIn(v.month).length })), default_view: 1 },
    bookings_mtd: { ...card('bookings_mtd', 'Bookings MTD', bookingViews.new.length, 'bookings', 'booking created this month (test rows and waitlist placeholders excluded, as in Reports)'), views: BOOKINGS_MTD_VIEWS.map(v => ({ ...v, value: bookingViews[v.key].length })) },
    revenue_mtd: { ...card('revenue_mtd', 'Revenue MTD', dollars(revenueViews.all), 'billing', 'completed payments dated this month on real bookings, as in Reports'), views: REVENUE_MTD_VIEWS.map(v => ({ ...v, value: dollars(revenueViews[v.key]) })) },
    // Prototype v108 P 955: the big number is every event in the next 7 days; "N with outstanding actions" links below it.
    events_next_7_outstanding: { ...card('events_next_7_outstanding', 'Events Next 7 Days', upcoming ? upcoming.length : next7.length, 'upcoming', 'events in the next 7 days; the link counts those with an outstanding action in any department', outstandingNext7 === null ? null : `${outstandingNext7} with outstanding actions`), outstanding: outstandingNext7 },
    outstanding_ar: card('outstanding_ar', 'Outstanding A/R', dollars(arCents), 'billing', 'remaining balance across active bookings, view formula'),
    sales_next_7: card('sales_next_7', 'Events Next 7 Days', salesNext7.length, 'bookings', 'event within 7 days with the logistics questionnaire not complete', 'GHL entry queue counts once its entity exists (§9)'),
    billing_next_7: card('billing_next_7', 'Events Next 7 Days', billingNext7.length, 'billing', 'event within 7 days with money still owed'),
    active_fulfillment: card('active_fulfillment', 'Active Fulfillment', fulfillment.length, 'editing', 'event passed, media not delivered'),
    overdue_editing: card('overdue_editing', 'Overdue Editing Actions', overdueEditingCount, 'editing', 'Editing board rows past their internal deadline (photo and video, sneak peeks and RAWs)'),
    pending_initial_review: card('pending_initial_review', 'Pending Initial Review', pendingInitialReview.length, 'editing', "the Editing board's Initial Review queue, one row per talent submission"),
    delivered_mtd: card('delivered_mtd', 'Delivered MTD', deliveredMtd.length, 'editing', 'media delivered this month'),
    editing_next_7: card('editing_next_7', 'Events Next 7 Days', 0, 'editing', 'events within 7 days with an Editing-owned action', 'Editing owns nothing before the event; stays 0 by rule'),
    // Trera, Oct 2026: new clients who signed and went to pay in the last 7 days but have no booking (Admin)
    unfinished_checkouts: unfinishedCheckouts === null ? null : card('unfinished_checkouts', 'Unfinished Checkouts', unfinishedCheckouts, 'unfinished', 'new clients who signed and went to pay in the last 7 days but have no booking (declined card or left the payment page)', 'last 7 days')
  }

  const byRole = {
    // Traci, 23 Sep 2026: the admin dashboard keeps only these; department detail lives in Department Actions.
    admin: ['active_bookings', 'events_next_31', 'events_this_month', 'bookings_mtd', 'revenue_mtd', 'events_next_7_outstanding', 'unfinished_checkouts'],
    sales: ['active_bookings', 'events_next_31', 'bookings_mtd', 'sales_next_7'],
    billing: ['active_bookings', 'events_this_month', 'bookings_mtd', 'revenue_mtd', 'outstanding_ar', 'billing_next_7'],
    editing: ['active_fulfillment', 'overdue_editing', 'pending_initial_review', 'delivered_mtd', 'editing_next_7'],
    talent_coordinator: ['active_bookings', 'events_next_7', 'events_next_31']
  }

  return { cards, byRole, counts: { active: active.length, fulfillment: fulfillment.length } }
}

/** The cards one role sees, in the handoff's order. */
export function kpisForRole(result, role) {
  // A card with no data behind it (unfinished checkouts when the count could not be read) is left out
  return (result.byRole[role] || result.byRole.admin).map(k => result.cards[k]).filter(Boolean)
}

/** §9 / §10 / §11.2 department action queues, and what each needs before it can be built. */
export const DEPARTMENT_QUEUES = {
  sales: [
    { key: 'sales_flags', title: 'Sales Flags', status: 'needs_entity', note: 'Flag entity (§7)' },
    { key: 'ghl_entry', title: 'New Bookings — Enter in GHL', status: 'needs_entity', note: 'GHL-entered event with immutable history (§9)' },
    { key: 'overdue_logistics', title: 'Upcoming Events — Overdue Logistics Questionnaire', status: 'computable', note: 'logistics_completed on the booking' }
  ],
  billing: [
    { key: 'billing_flags', title: 'Billing Flags', status: 'needs_entity', note: 'Flag entity (§7)' },
    { key: 'pay_talent', title: 'Media Approved — Pay Talent', status: 'needs_entity', note: 'Talent pay snapshot + deduction ledger (§6, §10.1)' },
    { key: 'qbo_invite', title: 'Send QBO Onboarding Invite', status: 'exists', note: 'on the current Billing dashboard' },
    { key: 'overdue_follow_up', title: 'Overdue Requiring Follow-Up', status: 'exists', note: 'Billing workbench: Overdue queue' },
    { key: 'invoicing_due_7', title: 'Invoicing — Due in 7 Days', status: 'exists', note: 'Billing workbench: Due in 7 days' }
  ],
  editing: [
    { key: 'initial_review', title: 'Uploaded Media Needing Initial Review', status: 'exists', note: 'current Editing dashboard; state machine locked in §11.2' },
    { key: 'with_issues', title: 'Uploaded Media with Issues', status: 'needs_entity', note: 'issue notes on the media submission (§11.7)' },
    { key: 'approved_in_progress', title: 'Approved & In Progress', status: 'exists', note: 'deliverable column needs the photo-count rule (§11.6; counts shipped in #485)' },
    { key: 'raws', title: 'RAWs', status: 'needs_rule', note: 'RAW due date from delivery/purchase (§11.8)' }
  ],
  admin: [],
  talent_coordinator: [
    { key: 'coordination', title: 'Coordination workbench', status: 'exists', note: 'Action Center, queues, clusters, talent list (#445–#456)' }
  ]
}
